import { useMemo, useState } from "react";
import { Spinner } from "@phosphor-icons/react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import type { SubmitHandler } from "react-hook-form";
import { useForm } from "react-hook-form";

import { updateBundle } from "@/api";
import { useBundles, useMaterials, useMedia, useMeditations } from "@/hooks";
import type { Bundle } from "@/types";
import {
  Button,
  ControlledDropzone,
  errorToast,
  Input,
  MultiSelect,
  Tabs,
} from "@/ui";
import type { Tab } from "@/ui";
import { convertToOptions } from "@/utils/selectOptions";

interface EditBundleFormProps {
  bundle: Bundle;
  onClose: () => void;
}

interface FormValues {
  name: string;
  image?: File;
  banner?: File;
}

export const EditBundleForm = ({
  bundle: initialBundle,
  onClose,
}: EditBundleFormProps) => {
  const queryClient = useQueryClient();
  const { useUpload } = useMedia();
  const { upload, isUploading } = useUpload();
  const { useBundle } = useBundles();
  const { data: bundleData } = useBundle(initialBundle.id);
  const bundle = bundleData ?? initialBundle;

  const { useAllMeditations } = useMeditations();
  const { data: meditationsData } = useAllMeditations(1, "");
  const meditationOptions = convertToOptions(meditationsData?.data ?? []);

  const { useAllMaterials } = useMaterials();
  const { data: materialsData } = useAllMaterials();
  const materialOptions = convertToOptions(materialsData?.data ?? []);

  const [selectedMeditations, setSelectedMeditations] = useState<string[]>(
    bundle.meditations?.map((m) => m.id) ?? [],
  );

  const [selectedMaterials, setSelectedMaterials] = useState<string[]>(
    bundle.materials?.map((m) => m.id) ?? [],
  );

  const { mutateAsync: updateBundleMutation, isPending } = useMutation({
    mutationFn: updateBundle,
  });

  const defaultValues = useMemo(
    () => ({
      name: bundle?.name || "",
    }),
    [bundle?.name],
  );

  const {
    formState: { errors, isValid },
    handleSubmit,
    register,
    control,
  } = useForm<FormValues>({
    mode: "onChange",
    defaultValues,
  });

  const onSubmit: SubmitHandler<FormValues> = async (data) => {
    try {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const bundleData: Record<string, any> = {
        name: data.name,
        type: bundle.type,
        tag: bundle.tag ?? "8 week course",
        materials: selectedMaterials,
        meditations: selectedMeditations,
      };

      await updateBundleMutation({
        bundleId: bundle.id,
        bundle: bundleData,
      });

      if (data.image) {
        const result = await upload(
          data.image,
          `/bundles/${bundle.id}/image/upload-url`,
        );
        if (!result.success) {
          errorToast("Failed to upload image. Please try again.");
          return;
        }
      }

      if (data.banner) {
        const result = await upload(
          data.banner,
          `/bundles/${bundle.id}/banner/upload-url`,
        );
        if (!result.success) {
          errorToast("Failed to upload banner. Please try again.");
          return;
        }
      }

      await queryClient.invalidateQueries({
        queryKey: ["bundles"],
      });
      onClose();
    } catch (error) {
      errorToast(error);
    }
  };

  const tabs: Tab[] = [
    {
      label: "Details",
      roles: ["admin", "partner"],
      component: (
        <div className="flex flex-col gap-y-8 py-4">
          <Input
            id="name"
            label="Bundle Name"
            placeholder="e.g., Mindfulness for Life"
            {...register("name", { required: "Bundle name is required" })}
            error={errors.name?.message}
            required
            className="text-neutral-900"
          />

          <div className="flex flex-col gap-2">
            <span className="text-sm font-medium text-neutral-700">Type</span>
            <p className="capitalize text-neutral-600">{bundle.type}</p>
          </div>

          <ControlledDropzone
            name="image"
            control={control}
            label="Replace Image (Optional)"
            placeholder="Upload new bundle image"
            accept={{ "image/*": [".png", ".jpg", ".jpeg"] }}
          />

          <ControlledDropzone
            name="banner"
            control={control}
            label="Replace Banner (Optional)"
            placeholder="Upload new bundle banner"
            accept={{ "image/*": [".png", ".jpg", ".jpeg"] }}
          />
        </div>
      ),
    },
    {
      label: "Materials",
      roles: ["admin", "partner"],
      component: (
        <div className="flex flex-col gap-y-4 py-4">
          <MultiSelect
            label="Assigned Materials"
            placeholder="Select materials..."
            options={materialOptions}
            selectedOptions={selectedMaterials}
            onChange={setSelectedMaterials}
          />
        </div>
      ),
    },
    {
      label: "Meditations",
      roles: ["admin", "partner"],
      component: (
        <div className="flex flex-col gap-y-4 py-4">
          <MultiSelect
            label="Assigned Meditations"
            placeholder="Select meditations..."
            options={meditationOptions}
            selectedOptions={selectedMeditations}
            onChange={setSelectedMeditations}
          />
        </div>
      ),
    },
  ];

  const [currentTabLabel, setCurrentTabLabel] = useState(tabs[0]?.label ?? "");

  return (
    <div className="flex flex-col gap-y-6">
      <Tabs
        tabs={tabs}
        userRole="admin"
        currentLabel={currentTabLabel}
        setCurrentLabel={setCurrentTabLabel}
        containerClassName="w-full"
      />

      <div className="flex justify-end gap-3 border-t border-neutral-200 pt-4">
        <Button onClick={onClose} variant="outlined" type="button">
          Cancel
        </Button>
        <Button
          onClick={handleSubmit(onSubmit)}
          disabled={!isValid || isPending || isUploading}
        >
          {isPending || isUploading ? (
            <Spinner className="h-5 w-5 animate-spin" />
          ) : (
            "Save Changes"
          )}
        </Button>
      </div>
    </div>
  );
};
